'use client';

import { useState, useEffect } from 'react';
import UrgentAlerts from './UrgentAlerts';
import StatsCard from './StatsCard';
import ActivityFeed from './ActivityFeed';
import QuickActions from './QuickActions';
import TodaysScheduleCalendar from './TodaysScheduleCalendar';
import UpcomingTasksList from './UpcomingTasksList';
import FamilyTasks from './FamilyTasks';
import UpcomingItems from './UpcomingItems';
import FamilyActivityIndicator from '@/components/common/FamilyActivityIndicator';
import QuickActionButton from '@/components/common/QuickActionButton';

interface Alert {
  id: string;
  type: 'medication' | 'checkin' | 'benefit' | 'document';
  title: string;
  description: string;
  timestamp: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  actionUrl: string;
  actionLabel: string;
}

interface Activity {
  id: string;
  type: 'task' | 'medication' | 'checkin' | 'document' | 'benefit';
  title: string;
  description: string;
  timestamp: string;
  user: {
    name: string;
    avatar: string;
    alt: string;
  };
  metadata?: {
    status?: string;
    value?: string;
  };
}

interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: string;
  href: string;
  color: 'primary' | 'secondary' | 'accent' | 'success';
}

interface ScheduleItem {
  id: string;
  time: string;
  title: string;
  type: 'medication' | 'appointment' | 'task';
  status: 'pending' | 'completed' | 'missed';
  details?: string;
}

interface Task {
  id: string;
  title: string;
  assignee: {
    name: string;
    avatar: string;
    alt: string;
  };
  dueDate: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in-progress' | 'completed';
}

interface UpcomingItem {
  id: string;
  title: string;
  date: string;
  type: 'appointment' | 'refill' | 'deadline' | 'checkin';
  description: string;
}

interface FamilyMember {
  id: string;
  name: string;
  avatar?: string;
  isOnline: boolean;
  lastActive?: string;
}

interface DashboardInteractiveProps {
  alerts: Alert[];
  activities: Activity[];
  quickActions: QuickAction[];
  scheduleItems: ScheduleItem[];
  familyTasks: Task[];
  upcomingItems: UpcomingItem[];
  familyMembers: FamilyMember[];
  onCreateTaskClick?: () => void;
}

const DashboardInteractive = ({
  alerts,
  activities,
  quickActions,
  scheduleItems,
  familyTasks,
  upcomingItems,
  familyMembers,
  onCreateTaskClick,
}: DashboardInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="h-8 bg-muted rounded w-48 mb-8 animate-pulse"></div>
          <div className="space-y-6">
            <div className="h-32 bg-muted rounded-lg animate-pulse"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-40 bg-muted rounded-lg animate-pulse"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 lg:pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-heading font-semibold text-foreground mb-2">
              Dashboard
            </h1>
            <p className="text-base font-caption text-muted-foreground">
              Welcome back! Here's what's happening with your parent's care today.
            </p>
          </div>
          <FamilyActivityIndicator members={familyMembers} showDetails />
        </div>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
              Urgent Alerts
            </h2>
            <UrgentAlerts alerts={alerts} />
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
              Key Metrics
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatsCard
                title="Medication Adherence"
                value="94%"
                subtitle="Last 30 days"
                icon="BeakerIcon"
                trend={{ value: 5, isPositive: true }}
                color="primary"
              />
              <StatsCard
                title="Wellness Score"
                value="8.2"
                subtitle="Latest check-in"
                icon="HeartIcon"
                trend={{ value: 3, isPositive: true }}
                color="success"
              />
              <StatsCard
                title="Pending Benefits"
                value="3"
                subtitle="Applications in progress"
                icon="GiftIcon"
                color="accent"
              />
              <StatsCard
                title="Next Appointment"
                value="2 days"
                subtitle="Dr. Johnson - Cardiology"
                icon="CalendarIcon"
                color="secondary"
              />
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  Recent Activity
                </h2>
                <div className="glass p-6">
                  <ActivityFeed activities={activities} />
                </div>
              </section>

              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  Quick Actions
                </h2>
                <QuickActions actions={quickActions} onCreateTaskClick={onCreateTaskClick} />
              </section>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <section>
                    <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                      Upcoming
                    </h2>
                    <div className="glass p-6">
                      <UpcomingItems items={upcomingItems} />
                    </div>
                  </section>
                </div>
                <div>
                  <section>
                    <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                      Family Tasks
                    </h2>
                    <div className="glass p-6">
                      <FamilyTasks tasks={familyTasks} />
                    </div>
                  </section>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                  Today&apos;s Schedule
                </h2>
                <TodaysScheduleCalendar />
              </section>

              <section>
                <UpcomingTasksList />
              </section>
            </div>
          </div>
        </div>
      </div>

      <QuickActionButton />
    </div>
  );
};

export default DashboardInteractive;